"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Plus, Search } from "lucide-react";
import { Service, ServicePrice, CartItem } from "@/types/service";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import {
  convertToMinutes,
  formattedDuration,
  getInitial,
} from "../../helper/utility";

interface ServiceListProps {
  services: Service[];
  cartItems: CartItem[];
  onAddToCart: (service: Service, price: ServicePrice) => void;
}

export function ServiceList({
  services,
  cartItems,
  onAddToCart,
}: ServiceListProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const formatPrice = (price: ServicePrice) => {
    if (price.amount === 0 || !price.currency) return "Free";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: price.currency,
    }).format(price.amount);
  };

  const isServiceInCart = (service: Service) => {
    return cartItems.some((item) => item.service.name === service.name);
  };

  const hasDifferentCurrency = (price: ServicePrice): boolean => {
    if (cartItems.length === 0) return false;
    const cartCurrency = cartItems[0]?.price?.currency;
    return (
      !!cartCurrency && !!price.currency && cartCurrency !== price.currency
    );
  };

  const filteredServices = services.filter(
    (service) =>
      service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search services..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>
      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            {filteredServices.map((service, serviceIndex) => (
              <div key={service.name}>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="w-full md:w-32 h-48 md:h-32 relative rounded-lg overflow-hidden flex-shrink-0">
                    <Avatar className="w-full h-full rounded-lg overflow-hidden">
                      <AvatarImage
                        src={service.image}
                        alt={service.name}
                        className="object-cover w-full h-full rounded-lg"
                      />
                      <AvatarFallback className="w-full h-full rounded-lg flex items-center justify-center">
                        {getInitial(service.name)}
                      </AvatarFallback>
                    </Avatar>
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold">{service.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {service.description}
                    </p>
                    <div className="space-y-3">
                      {service.prices.map((price, index) => {
                        const isAdded = isServiceInCart(service);
                        const differentCurrency = hasDifferentCurrency(price);
                        const isDisabled =
                          isAdded ||
                          differentCurrency ||
                          cartItems.some((x) => x.price?.amount > 0);

                        return (
                          <div
                            key={index}
                            className="flex items-center justify-between gap-4"
                          >
                            <div>
                              <div className="font-medium">
                                {formatPrice(price)}
                              </div>
                              <div className="text-sm text-muted-foreground flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formattedDuration(
                                  convertToMinutes(price.duration)
                                )}
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onAddToCart(service, price)}
                              disabled={isDisabled}
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              {isAdded
                                ? "Added"
                                : differentCurrency
                                ? "Different Currency"
                                : "Add"}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {serviceIndex < filteredServices.length - 1 && (
                  <Separator className="my-6" />
                )}
              </div>
            ))}
            {filteredServices.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No services found matching your search
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
